import {
  initAutoClearRequired,
  initDateRangeGuard,
  initDateFilterUrlSync,
  initTimeClear
} from './common.js';
import {
  openModal,
  closeModal
} from './modal.js';
import {
  ag
} from './aggrid.js';
import {
  registerInitFunctions,
} from './init.js';
import {
  showFlashMessage,
  dismissFlashMessage
} from './flashmessages.js';
import htmx from './htmxsetup.js';
import { hxEmitter } from './hxEventEmitter.js';
import { initCrmHandlerFilter } from './crmdashboard.js';
import { handlePdfDownloadRequest } from "./erpnext.js"
import { IStaticMethods } from "preline/preline";
import "./journalssr.js";
import "./fileviewer.js";
import "./threedrawingdepictor.js";
import "./formdraft.js";


// Ensure HSStaticMethods is set correctly
if (typeof window !== "undefined") {
  window.htmx = htmx;
  if (IStaticMethods) {
    window.HSStaticMethods = IStaticMethods;
  }
}

function voucherSeriesValueOf(selector) {
  if (!selector) return "";
  const el = document.querySelector(selector);
  return el ? (el.value || "") : "";
}

function voucherSeriesLabelOf(selector) {
  if (!selector) return "";
  const el = document.querySelector(selector);
  if (!el) return "";
  const value = el.value || "";
  const source = el.name ? document.getElementById(el.name) : null;
  if (!source || !source.dataset.options) return value;

  try {
    const options = JSON.parse(source.dataset.options || "[]");
    const found = options.find((opt) => String(opt.value) === String(value));
    return found && found.label ? found.label : value;
  } catch {
    return value;
  }
}

function addVoucherSeriesTriggerParams(event) {
  const btn = event.target && event.target.closest
    ? event.target.closest('[data-vs-trigger="voucher-series"]')
    : null;
  if (!btn) return;

  const id = voucherSeriesValueOf(btn.dataset.vsIdSelector) || "0";
  const params = event.detail && event.detail.parameters;
  if (!params) return;

  params.table_name = btn.dataset.vsTableName || "";
  params.doc_type = btn.dataset.vsDocType || "";
  params.code = voucherSeriesValueOf(btn.dataset.vsCodeSelector) || btn.dataset.vsCode || "";
  params.voucher_date = voucherSeriesValueOf(btn.dataset.vsDateSelector);
  params.voucher_id = id;
  params.voucher_no = btn.dataset.vsNoValue || voucherSeriesLabelOf(btn.dataset.vsNoSelector);
  params.tab = parseInt(id, 10) > 0 ? "change" : "settings";
  params.show_only_series = "0";
}

document.addEventListener("htmx:configRequest", addVoucherSeriesTriggerParams);

function onlyAllowNumericValue(e){
  const isInteger = e.step === '1' || e.step === 1;
  const allowNegative = e.dataset.allowNegative === '1';
  e.addEventListener('beforeinput', function (e) {
    const char = e.data;
    const value = this.value;
    
    if (e.inputType.startsWith("delete") || e.inputType === "insertLineBreak") return;
    const start = this.selectionStart || 0;
    const end = this.selectionEnd || start;
    const proposedValue = value.slice(0, start) + (char || '') + value.slice(end);
    if (allowNegative) {
      const valid = isInteger ? /^-?\d*$/.test(proposedValue) : /^-?\d*\.?\d*$/.test(proposedValue);
      if (!valid) e.preventDefault();
      return;
    }

    if (isInteger) {
      if (!char || !char.match(/[0-9]/)) { e.preventDefault(); return; }
    } else {
      if (!char || (!char.match(/[0-9.]/) || (char === '.' && value.includes('.')))) { e.preventDefault(); return; }
    }
  })
}

function initMaskedPlaceholder(el) {
  // Masked inputs show "***". On focus/click show the real typed value (if any)
  // or clear it so the user can type. On blur, mask back to "***" but keep the
  // real value in a data attribute so it can be submitted.
  if (!el || el.dataset?.maskedPlaceholder !== '1') return;

  const placeholder = el.dataset.maskedPlaceholderValue || '***';

  // Track real value (what user typed) separately from the displayed mask.
  if (el.dataset.maskedRealValue === undefined) {
    el.dataset.maskedRealValue = '';
  }

  const showRealForEditing = () => {
    // If currently masked, swap in the stored real value (or empty) for editing.
    if (el.value === placeholder) {
      el.value = el.dataset.maskedRealValue || '';
    }
  };

  const updateRealOnInput = () => {
    if (el.value !== placeholder) {
      el.dataset.maskedRealValue = el.value;
    }
  };

  const maskOnBlur = () => {
    // Store what the user typed and then mask the display.
    if (el.value !== placeholder) {
      el.dataset.maskedRealValue = el.value;
    }
    el.value = placeholder;
  };

  el.addEventListener('focus', showRealForEditing);
  el.addEventListener('click', showRealForEditing);
  el.addEventListener('input', updateRealOnInput);
  el.addEventListener('blur', maskOnBlur);

  // Before submit (native form submit), swap placeholder to real value (or 0).
  const form = el.closest('form');
  if (form && !form.dataset.maskedPlaceholderSubmitHandler) {
    form.dataset.maskedPlaceholderSubmitHandler = '1';
    form.addEventListener(
      'submit',
      () => {
        form.querySelectorAll('[data-masked-placeholder="1"]').forEach((inp) => {
          const ph = inp.dataset.maskedPlaceholderValue || '***';
          if (inp.value === ph) {
            const real = inp.dataset.maskedRealValue;
            inp.value = real && real !== ph ? real : '0';
          }
        });
      },
      true
    );
  }
}

function enforceMaxLength(el, maxLen, label) {
  if (!el) return;
  const max = Number(maxLen);
  if (!Number.isFinite(max) || max <= 0) return;

  // Hard-trim value so it never reaches server-side validation.
  const v = String(el.value ?? "");
  if (v.length <= max) {
    // Once user is back within limit, allow warning again later.
    delete el.dataset.maxlenWarned;
    return;
  }

  el.value = v.slice(0, max);

  // Show warning only once per "over-limit attempt" (prevents repeated toasts).
  if (el.dataset.maxlenWarned === "1") return;
  el.dataset.maxlenWarned = "1";

  const msg = `${label || "This field"} can be max ${max} characters.`;
  if (typeof window.showFlashMessage === "function") {
    window.showFlashMessage(null, "warning", `<p>${msg}</p>`, 4);
  }
}

function initLoginssrDevUnlock(root) {
  root = root || document;
  const builtBy = root.getElementById ? root.getElementById('loginssr-built-by') : null;
  if (!builtBy) return;
  if (builtBy.dataset.loginssrUnlockInstalled === '1') return;
  builtBy.dataset.loginssrUnlockInstalled = '1';

  let clicks = 0;

  function reveal() {
    const hijackInput = document.getElementById('hijackId');
    const testDbInput = document.getElementById('testDb');

    const hijackWrap = hijackInput ? hijackInput.closest('.hidden') : null;
    const testDbWrap = testDbInput ? testDbInput.closest('.hidden') : null;

    [hijackWrap, testDbWrap].forEach((el) => {
      if (!el) return;
      el.classList.remove('hidden');
    });

    if (testDbInput) {
      // Match old dev behavior: enable testDb after unlock.
      testDbInput.checked = true;
    }
  }

  builtBy.addEventListener('click', () => {
    clicks += 1;
    if (clicks >= 5) {
      reveal();
      clicks = 0;
    }
  });
}

function initPasswordToggles(root) {
  root = root || document;
  const buttons = root.querySelectorAll
    ? root.querySelectorAll('button[data-password-toggle]:not([data-password-toggle-installed])')
    : [];
  buttons.forEach((btn) => {
    btn.dataset.passwordToggleInstalled = '1';
    btn.addEventListener('click', () => {
      const targetId = btn.getAttribute('data-password-toggle');
      if (!targetId) return;
      const input = document.getElementById(targetId);
      if (!input) return;
      const isPwd = input.getAttribute('type') === 'password';
      input.setAttribute('type', isPwd ? 'text' : 'password');
      btn.classList.toggle('text-blue-600', isPwd);
      btn.classList.toggle('text-gray-500', !isPwd);
      const eyeOn = btn.querySelector('[data-password-eye-on]');
      const eyeOff = btn.querySelector('[data-password-eye-off]');
      if (eyeOn && eyeOff) {
        // If showing text, show "eye slash" to indicate it's visible.
        eyeOn.classList.toggle('hidden', !isPwd);
        eyeOff.classList.toggle('hidden', isPwd);
      }
    });
  });
}

function bindRestrictedInput(el, pattern) {
  if (!el || el.dataset.restrictedInputInstalled === '1') return;
  el.dataset.restrictedInputInstalled = '1';
  const clean = () => {
    const cleaned = el.value.replace(pattern, '');
    if (cleaned !== el.value) el.value = cleaned;
  };
  el.addEventListener('beforeinput', (event) => {
    if (event.inputType !== 'insertText' || !event.data) return;
    pattern.lastIndex = 0;
    if (pattern.test(event.data)) event.preventDefault();
  });
  el.addEventListener('input', clean);
  clean();
}

function bindLowercaseInput(el) {
  if (!el || el.dataset.lowercaseInputInstalled === '1') return;
  el.dataset.lowercaseInputInstalled = '1';
  const clean = () => {
    const cleaned = el.value.toLowerCase();
    if (cleaned === el.value) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    el.value = cleaned;
    if (document.activeElement === el && start !== null && end !== null) {
      el.setSelectionRange(start, end);
    }
  };
  el.addEventListener('input', clean);
  clean();
}

function initResetPasswordInputs() {
  bindRestrictedInput(document.getElementById('fp-email'), /\s/g);
  bindLowercaseInput(document.getElementById('fp-email'));
  bindRestrictedInput(document.getElementById('otp_field'), /\D/g);
  bindRestrictedInput(document.getElementById('new_password'), /\s/g);
  bindRestrictedInput(document.getElementById('confirm_password'), /\s/g);
  bindRestrictedInput(document.getElementById('old_password'), /\s/g);
}

function initPasswordStrength(root) {
  root = root || document;
  const newPwd = root.getElementById ? root.getElementById('new_password') : document.getElementById('new_password');
  const confirmPwd = root.getElementById ? root.getElementById('confirm_password') : document.getElementById('confirm_password');
  const resetBtn = root.getElementById ? root.getElementById('password_reset_btn') : document.getElementById('password_reset_btn');
  if (!newPwd || !confirmPwd || !resetBtn || newPwd.dataset.passwordStrengthInstalled === '1') return;
  newPwd.dataset.passwordStrengthInstalled = '1';

  function validatePassword() {
    const pwd = newPwd.value;
    const confirm = confirmPwd.value;
    const checks = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
      match: pwd.length > 0 && pwd === confirm,
    };

    Object.entries(checks).forEach(([key, valid]) => {
      const el = document.querySelector('[data-req="' + key + '"]');
      if (!el) return;
      el.querySelector('.req-icon').textContent = valid ? '\u2713' : '\u25CB';
      el.classList.toggle('text-green-600', valid);
      el.classList.toggle('text-gray-400', !valid);
    });

    resetBtn.disabled = !Object.values(checks).every(Boolean);
  }

  newPwd.addEventListener('input', validatePassword);
  confirmPwd.addEventListener('input', validatePassword);
  validatePassword();
}



(() => {
  window.htmx.onLoad(function() {
    if (window.HSStaticMethods && window.HSStaticMethods.autoInit) {
      window.HSStaticMethods.autoInit(['select']);
    }
  })
  window.openModal = openModal;
  window.closeModal = closeModal;
  // ---
  window.agAttachCellRenderFunctions = ag.attachCellRenderFunctions;
  window.agAwsAttachmentCell = ag.cellRenderer.awsAttachmentCell;
  window.agAwsModalEvent = ag.cellRenderer.awsModalEvent;
  window.agLinkCell = ag.cellRenderer.linkCell;
  window.agCommentBoxCell = ag.cellRenderer.commentBoxCell;
  window.agImageCell = ag.cellRenderer.imageCell;
  window.agSelectCell = ag.cellRenderer.selectCell;
  window.agRelatedTooltip = ag.cellRenderer.relatedTooltip;
  window.agColoredCell = ag.cellRenderer.coloredCell;
  window.agBadgeCell = ag.cellRenderer.badgeCell;
  window.agSectionsBadgeCell = ag.cellRenderer.sectionsBadgeCell;
  window.agNameWithDefaultBadge = ag.cellRenderer.nameWithDefaultBadge;
  window.ag = ag;

  
  // Make Grid constructor available globally for SSR templates
  window.Grid = ag.Grid;

  // ---
  window.showFlashMessage = showFlashMessage;
  window.dismissFlashMessage = dismissFlashMessage;
  window.registerInitFunctions = registerInitFunctions;
  window.hxEmitter = hxEmitter;
  window.handlePdfDownloadRequest = handlePdfDownloadRequest;
  window.onlyAllowNumericValue = onlyAllowNumericValue;
  window.initMaskedPlaceholder = initMaskedPlaceholder;
  window.enforceMaxLength = enforceMaxLength;
  window.htmx = htmx;

  // Party Master opens in an iframe from several forms. Keep the selected
  // party available to the caller while allowing its own change handlers to
  // load dependent addresses and defaults.
  window.applyPartyShortcutSelection = function (data, fieldName) {
    const partyId = data?.id || data?.customer_id || data?.vendor_id;
    const select = document.getElementById(fieldName);
    if (!partyId || !select) return false;

    let options = [];
    try {
      options = JSON.parse(select.dataset.options || '[]');
    } catch (_e) {
      options = [];
    }
    if (!options.some((option) => String(option.value) === String(partyId))) {
      const label = data.partyname || '';
      options.push({ label, text: label, value: String(partyId) });
    }
    select.dataset.options = JSON.stringify(options);
    if (typeof select.setOptions === 'function') select.setOptions(options);
    if (typeof select.setValue === 'function') select.setValue(String(partyId));
    else select.value = String(partyId);

    const input = document.getElementById(`${fieldName}_input`);
    if (input) {
      input.value = String(partyId);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  function _initAll(root) {
    initLoginssrDevUnlock(root || document);
    initPasswordToggles(root || document);
    initResetPasswordInputs();
    initPasswordStrength(root || document);
    initAutoClearRequired(root || document);
    initDateRangeGuard(root || document);
    initDateFilterUrlSync();
    initTimeClear(root || document);
    initCrmHandlerFilter(root || document);
  }

  // Initialize on initial load (after DOM ready) and after HTMX swaps.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { _initAll(document); });
  } else {
    _initAll(document);
  }

  document.addEventListener('htmx:afterSettle', function () {
    _initAll(document);
  });
})()
